<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import { useLocalizedRouter } from '@/composables/useLocalizedRouter'
import { useAppointmentStore } from '@/stores/appointmentStore'

const { t } = useI18n()
const { localizedPath } = useLocalizedRouter()
const appointmentStore = useAppointmentStore()

const isOpen = ref(false)
const panelId = 'contact-float-panel'

const CONTACT_EMAIL = 'contact@postdev.fr'
const PHONE_E164 = '+33616868688'
const PHONE_DISPLAY = '+33 6 16 86 86 88'

const toggle = () => {
  isOpen.value = !isOpen.value
}

const close = () => {
  isOpen.value = false
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') close()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div class="contact-float">
    <Transition name="contact-float-panel">
      <div
        v-show="isOpen"
        :id="panelId"
        class="contact-float__panel"
        role="dialog"
        :aria-label="t('contactFloat.title')"
      >
        <div class="contact-float__header">
          <div>
            <p class="contact-float__title">{{ t('contactFloat.title') }}</p>
            <p class="contact-float__subtitle">{{ t('contactFloat.subtitle') }}</p>
          </div>
          <button
            type="button"
            class="contact-float__close"
            :aria-label="t('contactFloat.toggleClose')"
            @click="close"
          >
            <i class="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <ul class="contact-float__list">
          <li>
            <a
              :href="`mailto:${CONTACT_EMAIL}`"
              class="contact-float__link"
              :aria-label="t('contactFloat.emailAria')"
            >
              <span class="contact-float__icon" aria-hidden="true">
                <i class="fas fa-envelope" />
              </span>
              <span class="contact-float__link-text">{{ CONTACT_EMAIL }}</span>
            </a>
          </li>
          <li>
            <a
              :href="`tel:${PHONE_E164}`"
              class="contact-float__link"
              :aria-label="t('contactFloat.phoneAria')"
            >
              <span class="contact-float__icon" aria-hidden="true">
                <i class="fas fa-phone" />
              </span>
              <span class="contact-float__link-text">{{ PHONE_DISPLAY }}</span>
            </a>
          </li>
        </ul>

        <div class="contact-float__actions">
          <RouterLink
            :to="localizedPath('/contact')"
            class="contact-float__btn contact-float__btn--secondary"
            @click="close"
          >
            <i class="fas fa-comment-dots" aria-hidden="true" />
            {{ t('contactFloat.contactPage') }}
          </RouterLink>
          <button
            type="button"
            class="contact-float__btn contact-float__btn--primary"
            @click="appointmentStore.openModal(); close()"
          >
            <i class="fas fa-calendar-check" aria-hidden="true" />
            {{ t('nav.bookCall') }}
          </button>
        </div>
      </div>
    </Transition>

    <button
      type="button"
      class="contact-float__fab"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      :aria-label="isOpen ? t('contactFloat.toggleClose') : t('contactFloat.toggleOpen')"
      @click="toggle"
    >
      <i v-if="!isOpen" class="fas fa-comment-dots" aria-hidden="true" />
      <i v-else class="fas fa-times" aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.contact-float {
  position: fixed;
  right: 1rem;
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 95;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  pointer-events: none;
}

.contact-float > * {
  pointer-events: auto;
}

.contact-float__panel {
  width: min(20rem, calc(100vw - 2rem));
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(var(--primary-color-rgb), 0.08);
  padding: 1rem 1rem 1.1rem;
  text-align: left;
}

.contact-float__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.contact-float__title {
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.25;
}

.contact-float__subtitle {
  font-size: 0.82rem;
  color: var(--text-secondary);
  margin: 0.25rem 0 0;
  line-height: 1.35;
  white-space: pre-line;
}

.contact-float__close {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  background: rgba(var(--primary-color-rgb), 0.06);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.contact-float__close:hover {
  background: rgba(var(--primary-color-rgb), 0.12);
  color: var(--primary-color);
}

.contact-float__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.contact-float__list li {
  margin-bottom: 0.5rem;
}

.contact-float__list li:last-child {
  margin-bottom: 0;
}

.contact-float__link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.35rem 0;
  border-radius: 6px;
  transition: color 0.2s ease;
  word-break: break-all;
}

.contact-float__link:hover {
  color: var(--primary-color);
}

.contact-float__icon {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 8px;
  background: rgba(var(--primary-color-rgb), 0.08);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
}

.contact-float__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(var(--primary-color-rgb), 0.1);
}

.contact-float__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.contact-float__btn--primary {
  background: var(--primary-color);
  color: #fff;
}

.contact-float__btn--primary:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.35);
}

.contact-float__btn--secondary {
  background: rgba(var(--primary-color-rgb), 0.08);
  color: var(--primary-color);
}

.contact-float__btn--secondary:hover {
  background: rgba(var(--primary-color-rgb), 0.15);
}

.contact-float__fab {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: var(--primary-color);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  transition: transform 0.2s ease, background 0.2s ease;
}

.contact-float__fab:hover {
  background: var(--primary-dark);
  transform: scale(1.05);
}

.contact-float__fab:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 3px;
}

.contact-float-panel-enter-active,
.contact-float-panel-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.contact-float-panel-enter-from,
.contact-float-panel-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 480px) {
  .contact-float {
    right: 0.75rem;
    bottom: max(0.75rem, env(safe-area-inset-bottom));
  }
}
</style>
